import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { LoadingBlock } from "../../components/Spinner";
import { IndexTabs, StatusTag } from "../../components/Drawing";
import { tableHeadRow,
  TRAINING_KINDS, TRAINING_RESULTS, labelOf, fmtDay, fmtDateOnly, todayInput, toDateInput, fromInput, ModalOverlay, ModalHeader, Field, MultiPick, EmptyState, input, textarea, secondaryBtn, dangerBtn,
  type TrainingRecord, type Option,
} from "./shared";
import { EntityTagsRow } from "../../components/EntityTags";
import { RelatedRecords } from "../../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../../components/NextSteps";
import { DetailFields, EditButton, RecordActions } from "../../components/RecordView";
import { TrainingAssignments } from "./TrainingAssignments";
import { RegisterToolbar, SummaryTiles } from "../../components/RegisterToolbar";
import { by, downloadCsv, matchesSearch, type SortOption } from "../../lib/registerExport";
import { REASON_LABELS, TrainingBreakdown, TrainingOverdueList, type TrainingInsightsView } from "./TrainingInsights";
import { PlaybookBar } from "../../components/PlaybookBar";

interface Props { canEdit: boolean; onChanged: () => void }

type View = "coverage" | "departments" | "overdue" | "records" | "assignments";
const VIEW_LABELS: Record<View, string> = { coverage: "Coverage by member", departments: "By department and site", overdue: "Overdue", records: "Records", assignments: "Assigned training" };

type Member = Coverage["members"][number];
const MEMBER_SORTS: SortOption<Member>[] = [
  { key: "name", label: "Name", compare: by.text((m) => m.name) },
  { key: "status", label: "Due first", compare: (a, b) => Number(a.inDate) - Number(b.inDate) || a.name.localeCompare(b.name) },
  { key: "completed", label: "Last completed", compare: by.date((m) => m.awarenessCompletedAt, "desc") },
];
const RECORD_SORTS: SortOption<TrainingRecord>[] = [
  { key: "completed", label: "Completed (newest)", compare: by.date((r) => r.completedAt, "desc") },
  { key: "expires", label: "Expires (soonest)", compare: by.date((r) => r.expiresAt, "asc") },
  { key: "person", label: "Person", compare: by.text((r) => personOf(r)) },
  { key: "kind", label: "Type", compare: (a, b) => labelOf(TRAINING_KINDS, a.kind).localeCompare(labelOf(TRAINING_KINDS, b.kind)) },
];

interface Coverage {
  members: Array<{ id: string; name: string; email: string | null; awarenessCompletedAt: string | null; awarenessExpiresAt: string | null; inDate: boolean }>;
  covered: number;
  total: number;
  phishing: { simulations: number; clicked: number; reported: number; clickRate: number | null };
}

type Form = { userId: string; personName: string; personEmail: string; kind: string; title: string; provider: string; completedAt: string; expiresAt: string; result: string; score: string; notes: string };
const EMPTY: Form = { userId: "", personName: "", personEmail: "", kind: "security_awareness", title: "", provider: "", completedAt: todayInput(), expiresAt: "", result: "passed", score: "", notes: "" };

export function TrainingTab({ canEdit, onChanged }: Props) {
  const [records, setRecords] = useState<TrainingRecord[] | null>(null);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [members, setMembers] = useState<Option[]>([]);
  const [editing, setEditing] = useState<{ record: TrainingRecord | null; userId?: string } | null>(null);
  // The record open in its read-only view (a row click or ?record=<id>).
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [bulk, setBulk] = useState(false);
  // Assigned courses first when the page is opened from an assignment link (?assignment=<id>).
  // A phishing training proposal link (?proposal=<departmentId>) or a competence one (?competence=<requirementId>) opens the same view.
  const [view, setView] = useState<View>(() => {
    const q = new URLSearchParams(window.location.search);
    return q.has("assignment") || q.has("proposal") || q.has("competence") ? "assignments" : "coverage";
  });
  const [insights, setInsights] = useState<TrainingInsightsView | null>(null);
  const [search, setSearch] = useState("");
  const [memberSort, setMemberSort] = useState("status");
  const [recordSort, setRecordSort] = useState("completed");
  /** Tile filter on the member view: only those in date, or only those due. */
  const [memberFilter, setMemberFilter] = useState<"in_date" | "due" | null>(null);
  const [kindFilter, setKindFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  // ?record=<id>: a cross-reference elsewhere linked straight to one training record.
  const [searchParams, setSearchParams] = useSearchParams();
  const recordParam = searchParams.get("record");
  const assignmentParam = searchParams.get("assignment");
  // A "Remind" link (?assignment=<id>) from the overdue list opens the assigned training view.
  useEffect(() => { if (assignmentParam) setView("assignments"); }, [assignmentParam]);

  const load = useCallback(async () => {
    try {
      const [r, c, i] = await Promise.all([
        api.get<{ records: TrainingRecord[] }>("/resilience/training"),
        api.get<Coverage>("/resilience/training/coverage"),
        api.get<TrainingInsightsView>("/resilience/training/insights").catch(() => null),
      ]);
      setRecords(r.records); setCoverage(c); setInsights(i);
      setMembers(c.members.map((m) => ({ id: m.id, name: m.name })));
      setError(null);
    } catch (e) { setError(apiErrorMessage(e, "Could not load training records")); setRecords([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!recordParam || !records) return;
    const hit = records.find((r) => r.id === recordParam);
    if (hit) { setView("records"); setViewingId(hit.id); }
    const next = new URLSearchParams(searchParams); next.delete("record"); setSearchParams(next, { replace: true });
  }, [recordParam, records]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(f: Form, existing: TrainingRecord | null) {
    const payload = {
      userId: f.userId || null, personName: f.userId ? null : f.personName || null, personEmail: f.userId ? null : f.personEmail || null,
      kind: f.kind, title: f.title.trim(), provider: f.provider || null, completedAt: fromInput(f.completedAt) ?? new Date().toISOString(), expiresAt: fromInput(f.expiresAt),
      result: f.result || null, score: f.score.trim() === "" ? null : Number(f.score), notes: f.notes || null,
    };
    if (existing) await api.put(`/resilience/training/${existing.id}`, payload); else await api.post("/resilience/training", payload);
    // An edit returns to the record's view (still open underneath); a new record returns to the list.
    setEditing(null); await load(); onChanged();
  }

  async function remove(r: TrainingRecord) {
    if (!window.confirm(`Delete "${r.title}" for ${personOf(r)}?`)) return;
    await api.del(`/resilience/training/${r.id}`); setViewingId(null); await load(); onChanged();
  }
  const viewing = viewingId ? records?.find((r) => r.id === viewingId) ?? null : null;

  if (!records || !coverage) return <LoadingBlock />;
  const pct = coverage.total ? Math.round((coverage.covered / coverage.total) * 100) : null;
  const overdue = insights?.overdue ?? [];
  const chooseView = (v: View) => { setView(v); setSearch(""); };

  const shownMembers = coverage.members
    .filter((m) => (memberFilter === "in_date" ? m.inDate : memberFilter === "due" ? !m.inDate : true) && matchesSearch(search, m.name, m.email))
    .sort((MEMBER_SORTS.find((s) => s.key === memberSort) ?? MEMBER_SORTS[0]).compare);
  const shownRecords = records
    .filter((r) => (!kindFilter || r.kind === kindFilter) && matchesSearch(search, personOf(r), r.title, r.provider, labelOf(TRAINING_KINDS, r.kind), r.result))
    .sort((RECORD_SORTS.find((s) => s.key === recordSort) ?? RECORD_SORTS[0]).compare);
  const shownOverdue = overdue.filter((o) => matchesSearch(search, o.name, o.email, ...o.assignments.map((a) => a.title)));

  const exportRecords = () => downloadCsv<TrainingRecord>("training-records", [
    { header: "Person", value: (r) => personOf(r) },
    { header: "Email", value: (r) => r.user?.email ?? r.personEmail },
    { header: "Training", value: (r) => r.title },
    { header: "Type", value: (r) => labelOf(TRAINING_KINDS, r.kind) },
    { header: "Provider", value: (r) => r.provider },
    { header: "Completed", value: (r) => r.completedAt.slice(0, 10) },
    { header: "Expires", value: (r) => r.expiresAt?.slice(0, 10) },
    { header: "Result", value: (r) => (r.result ? labelOf(TRAINING_RESULTS, r.result) : "") },
    { header: "Score", value: (r) => r.score },
  ], shownRecords);
  const exportMembers = () => downloadCsv<Member>("training-coverage", [
    { header: "Member", value: (m) => m.name },
    { header: "Email", value: (m) => m.email },
    { header: "Awareness completed", value: (m) => m.awarenessCompletedAt?.slice(0, 10) },
    { header: "Expires", value: (m) => m.awarenessExpiresAt?.slice(0, 10) },
    { header: "In date", value: (m) => m.inDate },
  ], shownMembers);
  const exportOverdue = () => downloadCsv("training-overdue", [
    { header: "Member", value: (o: (typeof overdue)[number]) => o.name },
    { header: "Email", value: (o: (typeof overdue)[number]) => o.email },
    { header: "Why", value: (o: (typeof overdue)[number]) => o.reasons.map((r) => REASON_LABELS[r]).join("; ") },
    { header: "Awareness completed", value: (o: (typeof overdue)[number]) => o.awarenessCompletedAt?.slice(0, 10) },
    { header: "Overdue assignments", value: (o: (typeof overdue)[number]) => o.assignments.map((a) => `${a.title} (due ${a.dueAt.slice(0, 10)})`).join("; ") },
  ], shownOverdue);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PlaybookBar ids={["security-awareness"]} />
      <SummaryTiles tiles={[
        { key: "in_date", label: "Awareness coverage", value: pct != null ? `${pct}%` : "—", hint: `${coverage.covered} of ${coverage.total} members in date`, tone: pct == null ? "neutral" : pct >= 90 ? "success" : pct >= 70 ? "warning" : "danger", active: view === "coverage" && memberFilter === "in_date", onClick: () => { chooseView("coverage"); setMemberFilter((f) => (f === "in_date" ? null : "in_date")); } },
        { key: "due", label: "Due", value: coverage.total - coverage.covered, hint: "no in-date awareness training", tone: coverage.total - coverage.covered ? "warning" : "neutral", active: view === "coverage" && memberFilter === "due", onClick: () => { chooseView("coverage"); setMemberFilter((f) => (f === "due" ? null : "due")); } },
        { key: "overdue", label: "To chase", value: insights ? overdue.length : "—", hint: "out of date or an assignment past due", tone: overdue.length ? "danger" : "neutral", active: view === "overdue", onClick: () => chooseView(view === "overdue" ? "coverage" : "overdue") },
        { key: "phishing", label: "Phishing click rate", value: coverage.phishing.clickRate != null ? `${coverage.phishing.clickRate}%` : "—", hint: `${coverage.phishing.simulations} results in 12 months · ${coverage.phishing.reported} reported`, active: view === "departments", onClick: () => chooseView(view === "departments" ? "coverage" : "departments") },
        { key: "records", label: "Records", value: records.length, hint: "all kinds", active: view === "records", onClick: () => chooseView(view === "records" ? "coverage" : "records") },
      ]} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <IndexTabs
          items={(["coverage", "departments", "overdue", "records", "assignments"] as const).map((v) => ({
            id: v, label: VIEW_LABELS[v], count: v === "records" ? records.length : v === "overdue" && overdue.length ? overdue.length : undefined,
          }))}
          value={view}
          onChange={chooseView}
          style={{ marginBottom: 0, flex: "1 1 auto", minWidth: 0 }}
        />
        {canEdit && view !== "assignments" && <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={() => setBulk(true)}><Users size={12} style={{ verticalAlign: -2, marginRight: 6 }} />Record a session for many</button>
          <button type="button" style={btnPrimary} onClick={() => setEditing({ record: null })}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Add record</button>
        </div>}
      </div>
      {error && <div style={{ ...text.body, ...card, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      {view === "assignments" ? (
        <TrainingAssignments canEdit={canEdit} />
      ) : view === "departments" ? (
        insights ? <TrainingBreakdown insights={insights} /> : <LoadingBlock />
      ) : view === "overdue" ? (
        <div>
          <RegisterToolbar search={search} onSearch={setSearch} placeholder="Search member or assignment" count={{ shown: shownOverdue.length, total: overdue.length }} onExport={exportOverdue} />
          {insights ? <TrainingOverdueList rows={shownOverdue} canEdit={canEdit} onRecord={(userId) => setEditing({ record: null, userId })} /> : <LoadingBlock />}
        </div>
      ) : view === "coverage" ? (
        <div>
        <RegisterToolbar search={search} onSearch={setSearch} placeholder="Search member" sortOptions={MEMBER_SORTS} sort={memberSort} onSort={setMemberSort} count={{ shown: shownMembers.length, total: coverage.members.length }} onExport={exportMembers}>
          {memberFilter && <button type="button" style={secondaryBtn} onClick={() => setMemberFilter(null)}>{memberFilter === "due" ? "Showing due only" : "Showing in date only"} · clear</button>}
        </RegisterToolbar>
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={tableHeadRow}>
              {["Member", "Awareness training", "Expires", "Status", ""].map((h) => <th key={h} style={{ padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {shownMembers.map((m) => (
                <tr key={m.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                  <td style={{ padding: "10px 14px", color: theme.color.text, fontWeight: 600 }}>{m.name}<div style={{ ...text.meta, fontWeight: 400 }}>{m.email}</div></td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted }}>{m.awarenessCompletedAt ? `completed ${fmtDay(m.awarenessCompletedAt)}` : "none recorded"}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted }}>{m.awarenessExpiresAt ? fmtDay(m.awarenessExpiresAt) : m.awarenessCompletedAt ? "12 months from completion" : "—"}</td>
                  <td style={{ padding: "10px 14px" }}><StatusTag tone={m.inDate ? "met" : "gap"}>{m.inDate ? "In date" : "Due"}</StatusTag></td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{canEdit && !m.inDate && <button type="button" style={secondaryBtn} onClick={() => setEditing({ record: null, userId: m.id })} title="Record this member's awareness training">Record training</button>}</td>
                </tr>
              ))}
              {shownMembers.length === 0 && <tr><td colSpan={5} style={{ padding: 20, color: theme.color.muted }}>{coverage.members.length === 0 ? "No active members." : "Nothing matches."}</td></tr>}
            </tbody>
          </table>
        </div>
        </div>
      ) : records.length === 0 ? (
        <div style={card}><EmptyState title="No training recorded" detail="Record awareness courses, inductions, board briefings, role-specific training and phishing simulation results per person. Coverage feeds ISO 27001 7.2 / 7.3 / A.6.3, the NCSC CAF B6 outcomes and the executive report." action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setEditing({ record: null })}>Add the first record</button> : undefined} /></div>
      ) : (
        <div>
        <RegisterToolbar search={search} onSearch={setSearch} placeholder="Search person, course, provider" sortOptions={RECORD_SORTS} sort={recordSort} onSort={setRecordSort} count={{ shown: shownRecords.length, total: records.length }} onExport={exportRecords}>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} aria-label="Filter by type" style={{ ...input, width: "auto", fontSize: text.hint.fontSize, padding: "7px 10px" }}>
            <option value="">All types</option>
            {TRAINING_KINDS.filter((k) => records.some((r) => r.kind === k.id)).map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </RegisterToolbar>
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={tableHeadRow}>
              {["Person", "Training", "Type", "Completed", "Expires", "Result"].map((h, i) => <th key={i} style={{ padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {shownRecords.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: theme.color.muted }}>Nothing matches.</td></tr>}
              {shownRecords.map((r) => (
                <tr key={r.id} onClick={() => setViewingId(r.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                  <td style={{ padding: "10px 14px", color: theme.color.text, fontWeight: 600 }}>{personOf(r)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.text }}>
                    {r.title}{r.provider && <div style={text.meta}>{r.provider}</div>}
                    <div onClick={(e) => e.stopPropagation()}><EntityTagsRow entity={{ kind: "training", id: r.id }} readOnly style={{ marginTop: 4, marginBottom: 0 }} /></div>
                  </td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted }}>{labelOf(TRAINING_KINDS, r.kind)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDateOnly(r.completedAt)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDateOnly(r.expiresAt)}</td>
                  <td style={{ padding: "10px 14px", color: theme.color.muted }}>{r.result ? labelOf(TRAINING_RESULTS, r.result) : "—"}{r.score != null ? ` · ${r.score}%` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {viewing && !editing && <RecordDetail record={viewing} canEdit={canEdit} onClose={() => setViewingId(null)} onEdit={() => setEditing({ record: viewing })} onDelete={() => void remove(viewing)} />}
      {editing && <RecordForm initial={editing.record ? toForm(editing.record) : { ...EMPTY, userId: editing.userId ?? "" }} existing={editing.record} members={members} onClose={() => setEditing(null)} onSave={save} />}
      {bulk && <BulkForm members={members} onClose={() => setBulk(false)} onSaved={async () => { setBulk(false); await load(); onChanged(); }} />}
    </div>
  );
}

/** One training record, read-only; Edit and Delete for those who may change it. */
function RecordDetail({ record: r, canEdit, onClose, onEdit, onDelete }: { record: TrainingRecord; canEdit: boolean; onClose: () => void; onEdit: () => void; onDelete: () => void }) {
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`${r.title}: ${personOf(r)}`} onClose={onClose} right={canEdit ? <EditButton onClick={onEdit} /> : undefined} />
      <div style={{ padding: 20, display: "grid", gap: 12 }}>
        <DetailFields fields={[
          { label: "Person", value: personOf(r) },
          { label: "Email", value: r.user?.email ?? r.personEmail },
          { label: "Type", value: labelOf(TRAINING_KINDS, r.kind) },
          { label: "Provider", value: r.provider },
          { label: "Completed", value: fmtDateOnly(r.completedAt) },
          { label: "Expires", value: r.expiresAt ? fmtDateOnly(r.expiresAt) : r.kind === "security_awareness" ? "12 months from completion" : null },
          { label: "Result", value: r.result ? labelOf(TRAINING_RESULTS, r.result) : null },
          { label: "Score", value: r.score != null ? `${r.score}%` : null },
          { label: "Notes", value: r.notes, wide: true, multiline: true },
        ]} />
        <div>
          <EntityTagsRow entity={{ kind: "training", id: r.id }} readOnly={!canEdit} />
          <RelatedRecords entity={{ kind: "training", id: r.id }} defaultOpen={false} />
        </div>
        {canEdit && (
          <RecordActions>
            <button type="button" style={dangerBtn} onClick={onDelete}>Delete record</button>
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

const personOf = (r: TrainingRecord) => r.user?.displayName || r.user?.email || r.personName || r.personEmail || "—";
const toForm = (r: TrainingRecord): Form => ({ userId: r.userId ?? "", personName: r.personName ?? "", personEmail: r.personEmail ?? "", kind: r.kind, title: r.title, provider: r.provider ?? "", completedAt: toDateInput(r.completedAt), expiresAt: toDateInput(r.expiresAt), result: r.result ?? "", score: r.score?.toString() ?? "", notes: r.notes ?? "" });

function RecordForm({ initial, existing, members, onClose, onSave }: { initial: Form; existing: TrainingRecord | null; members: Option[]; onClose: () => void; onSave: (f: Form, existing: TrainingRecord | null) => Promise<void> }) {
  const [f, setF] = useState<Form>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => ({ ...p, [k]: v }));
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try { await onSave(f, existing); } catch (err) { setError(apiErrorMessage(err, "Could not save the record")); setSaving(false); }
  }
  // Worked out from the form as it is filled in, so each tick lands as the field does.
  const phishing = f.kind === "phishing_simulation";
  const steps: NextStepItem[] = [
    { done: !!(f.userId || f.personName || f.personEmail), label: "Name the person", hint: "Pick a member so the coverage table counts it." },
    { done: !!f.title.trim(), label: "Name the course or session" },
    { done: !!f.result, label: phishing ? "Record whether they clicked or reported" : "Record the result" },
    { done: !!f.expiresAt || f.kind === "security_awareness" || phishing, label: "Set when it expires", hint: "Without an expiry nobody is reminded to refresh it." },
    { done: !!f.provider, label: "Record the provider" },
  ];
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={existing ? "Edit training record" : "Add training record"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / -1" }}><NextSteps items={steps} /></div>
        <Field label="Member" span2 help="Or name someone without a login below (contractor, board member)."><select value={f.userId} onChange={(e) => set("userId", e.target.value)} style={input}><option value="">Not a member</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        {!f.userId && <Field label="Name"><input value={f.personName} onChange={(e) => set("personName", e.target.value)} style={input} /></Field>}
        {!f.userId && <Field label="Email"><input type="email" value={f.personEmail} onChange={(e) => set("personEmail", e.target.value)} style={input} /></Field>}
        <Field label="Type *"><select value={f.kind} onChange={(e) => set("kind", e.target.value)} style={input}>{TRAINING_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select></Field>
        <Field label="Result"><select value={f.result} onChange={(e) => set("result", e.target.value)} style={input}><option value="">—</option>{TRAINING_RESULTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></Field>
        <Field label="Course / session *" span2><input required value={f.title} onChange={(e) => set("title", e.target.value)} style={input} placeholder="e.g. Annual security awareness 2026" /></Field>
        <Field label="Provider"><input value={f.provider} onChange={(e) => set("provider", e.target.value)} style={input} /></Field>
        <Field label="Score (%)"><input type="number" min={0} max={100} value={f.score} onChange={(e) => set("score", e.target.value)} style={input} /></Field>
        <Field label="Completed *"><input type="date" required value={f.completedAt} onChange={(e) => set("completedAt", e.target.value)} style={input} /></Field>
        <Field label="Expires" help="Awareness training without an expiry counts for twelve months."><input type="date" value={f.expiresAt} min={f.completedAt || undefined} onChange={(e) => set("expiresAt", e.target.value)} style={input} /></Field>
        <Field label="Notes" span2><textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} style={textarea} /></Field>
        {existing && <div style={{ gridColumn: "1 / -1" }}>
          <EntityTagsRow entity={{ kind: "training", id: existing.id }} />
          <RelatedRecords entity={{ kind: "training", id: existing.id }} defaultOpen={false} />
        </div>}
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>

          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function BulkForm({ members, onClose, onSaved }: { members: Option[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const [userIds, setUserIds] = useState<string[]>([]);
  const [kind, setKind] = useState("security_awareness");
  const [title, setTitle] = useState("");
  const [provider, setProvider] = useState("");
  const [completedAt, setCompletedAt] = useState(todayInput());
  const [expiresAt, setExpiresAt] = useState("");
  const [result, setResult] = useState("attended");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      await api.post("/resilience/training/bulk", { userIds, kind, title: title.trim(), provider: provider || null, completedAt: fromInput(completedAt) ?? new Date().toISOString(), expiresAt: fromInput(expiresAt), result: result || null });
      await onSaved();
    } catch (err) { setError(apiErrorMessage(err, "Could not save")); setSaving(false); }
  }
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Record a session for many members" onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Members *" span2>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <button type="button" style={secondaryBtn} onClick={() => setUserIds(members.map((m) => m.id))}>Everyone</button>
            <button type="button" style={secondaryBtn} onClick={() => setUserIds([])}>None</button>
          </div>
          <MultiPick options={members} selected={userIds} onChange={setUserIds} empty="No active members" />
        </Field>
        <Field label="Type *"><select value={kind} onChange={(e) => setKind(e.target.value)} style={input}>{TRAINING_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select></Field>
        <Field label="Result"><select value={result} onChange={(e) => setResult(e.target.value)} style={input}>{TRAINING_RESULTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></Field>
        <Field label="Course / session *" span2><input required value={title} onChange={(e) => setTitle(e.target.value)} style={input} /></Field>
        <Field label="Provider"><input value={provider} onChange={(e) => setProvider(e.target.value)} style={input} /></Field>
        <Field label="Completed *"><input type="date" required value={completedAt} onChange={(e) => setCompletedAt(e.target.value)} style={input} /></Field>
        <Field label="Expires" span2><input type="date" value={expiresAt} min={completedAt || undefined} onChange={(e) => setExpiresAt(e.target.value)} style={input} /></Field>
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving || userIds.length === 0} style={{ ...btnPrimary, opacity: saving || userIds.length === 0 ? 0.6 : 1 }}>{saving ? "Saving…" : `Record for ${userIds.length}`}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}
